import React, {
  ChangeEvent,
  MouseEvent,
  MutableRefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Section from '../../../../components/Main/Section/Section';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  SelectChangeEvent,
  Snackbar,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import Guide from '../../../../components/Guide/Guide';
import { ReactComponent as TrustpageGuidePlaceholder } from '../../../../assets/images/trustpage_no_content.svg';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, Repeat } from '@mui/icons-material';
import { ReactComponent as FlagDe } from '../../../../assets/images/flags/de.svg';
import { ReactComponent as FlagGb } from '../../../../assets/images/flags/gb.svg';
import { ReactComponent as FlagNl } from '../../../../assets/images/flags/nl.svg';
import MonitoringMultiSelect from '../../../../components/MonitoringMultiSelect/MonitoringMultiSelect';
import { IMonitoring } from '../../../../shared/interfaces/IMonitoring';
import ReviewMonitoringService from '../../../../services/ReviewMonitoringService';
import { AuthContext } from '../../../../providers/AuthProvider';
import { IDesign } from '../../../../shared/interfaces/IDesign';
import TrustpageService from '../../../../services/TrustpageService';
import PlatformService from '../../../../services/PlatformService';
import { IPlatform } from '../../../../shared/interfaces/IPlatform';
import { IGMBLocationsResponse } from '../../../../shared/interfaces/IGMBLocationsResponse';
import GMBService from '../../../../services/GMBService';
import TrustpageDesignChanger from '../../../../components/TrustpageDesignChanger/TrustpageDesignChanger';

const defaultDesign: IDesign = {
  companyLogo: '',
  primaryColor: '#357E8B',
  secondaryColor: '#000000',
  customFont: 'Courier New',
  customFontFormat: '',
};

const TrustpageGuide = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [configuredMonitorings, setConfiguredMonitorings] = useState<IMonitoring[]>([]);
  const [selectedMonitorings, setSelectedMonitorings] = useState<string[]>([]);
  const [companyName, setCompanyName] = useState<string>('');
  const [companyWebsiteUrl, setCompanyWebsiteUrl] = useState<string>('');
  const [editDesign, setEditDesign] = useState<boolean>(false);
  const [design, setDesign] = useState<IDesign>(defaultDesign);
  const [editContent, setEditContent] = useState(false);
  const aboutCompanyTextRef = useRef<HTMLInputElement | null>(null);
  const copyrightTextRef = useRef<HTMLInputElement | null>(null);
  const privacyPolicyLinkRef = useRef<HTMLInputElement | null>(null);
  const imprintLinkRef = useRef<HTMLInputElement | null>(null);
  const contactEmailRef = useRef<HTMLInputElement | null>(null);
  const [language, setLanguage] = useState('de');
  const [currentSurveyGuideStep, setCurrentSurveyGuideStep] = useState(0);
  const [showCreateError, setShowCreateError] = useState<boolean>(false);

  const handleCreateErrorClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowCreateError(false);
  };

  const handleNextSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep + 1);
  };

  const handlePreviousSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep - 1);
  };

  const handleSelectedMonitoringChange = (event: SelectChangeEvent<typeof selectedMonitorings>) => {
    const {
      target: { value },
    } = event;
    setSelectedMonitorings(typeof value === 'string' ? value.split(',') : value);
  };

  const isValidUrl = (url: string) => {
    return url.startsWith('http://') || url.startsWith('https://');
  };

  const correctContentUrl = (ref: MutableRefObject<HTMLInputElement | null>) => {
    if (editContent) {
      if (ref.current && ref.current.value && !isValidUrl(ref.current.value)) {
        ref.current.value = `https://${ref.current.value}`;
      }
    }
  };
  const handleAddTrustPageClick = async () => {
    const monitorings = selectedMonitorings.map((monitoringId) => ({
      id: configuredMonitorings.filter((input) => input.id === monitoringId)[0].id,
      type: configuredMonitorings.filter((input) => input.id === monitoringId)[0].type
        ? 'LOCATION'
        : 'MONITORING',
    }));

    setShowCreateError(false);
    setLoading(true);
    const success = await TrustpageService.addTrustpage(
      companyName,
      companyWebsiteUrl,
      monitorings,
      design.companyLogo,
      design.primaryColor,
      design.secondaryColor,
      design.customFont,
      design.customFontFormat,
      aboutCompanyTextRef.current?.value || '',
      copyrightTextRef.current?.value || '',
      privacyPolicyLinkRef.current?.value || '',
      imprintLinkRef.current?.value || '',
      contactEmailRef.current?.value || '',
      language
    );

    if (success) {
      setCompanyName('');
      setCompanyWebsiteUrl('');
      setSelectedMonitorings([]);
      setEditDesign(false);
      setEditContent(false);
      setLanguage('de');
    } else {
      setShowCreateError(true);
    }

    setLoading(false);
    return success;
  };

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async () => {
      const response = await PlatformService.getPlatforms(setLoading, controller.signal);
      if (!Array.isArray(response)) return;

      setPlatforms(response);
    };

    const getMonitorings = async () => {
      let monitorings: IMonitoring[] = await ReviewMonitoringService.getMonitoring(
        false,
        'both',
        setLoading,
        controller.signal
      );

      if (!monitorings) return;

      const gmbMonitoredLocations: IGMBLocationsResponse = await GMBService.getAllLocations(
        undefined,
        1000,
        setLoading,
        controller.signal,
        true
      );

      if (!gmbMonitoredLocations) return;

      const hasGmbEntries = gmbMonitoredLocations.locations.length > 0;

      if (hasGmbEntries) {
        for (const location of gmbMonitoredLocations.locations) {
          const mon: IMonitoring = {
            company: location.locationID,
            createdAt: '',
            deletedAt: '',
            id: location.ID,
            jobs: [],
            platformId: -2,
            label: location.locationData.title,
            url: '',
            updatedAt: '',
            communityManagement: location.communityManagement,
            isBenchmark: false,
            type: 'LOCATION',
          };
          monitorings.push(mon);
        }

        setPlatforms((prevState) => [...prevState, PlatformService.getPseudoGoogle()]);
      }

      if (monitorings.length === 0) return;

      setConfiguredMonitorings(monitorings);
      setPlatforms((prevState) => {
        return [...prevState].filter((v) => monitorings.find((m) => m.platformId === v.id));
      });
    };

    (async () => {
      await getPlatforms();
      await getMonitorings();
    })();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!hasRole('can-add-trustpage')) {
      navigate('/');
    }
  }, [hasRole, navigate]);

  return (
    <Section>
      {loading && (
        <Box>
          <LinearProgress sx={{ my: theme.spacing(1) }} />
        </Box>
      )}
      <Guide
        image={
          <TrustpageGuidePlaceholder
            height="180"
            style={{ marginBottom: `${theme.spacing(6)}` }}
          />
        }
      >
        <Box sx={{ maxWidth: '600px' }}>
          <Stepper
            activeStep={currentSurveyGuideStep}
            orientation="vertical"
          >
            <Step>
              <StepLabel>{t('company_data_label')}</StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                  <TextField
                    label={t('company_name_label')}
                    size="small"
                    variant="outlined"
                    placeholder={t('company_name_placeholder')}
                    value={companyName}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setCompanyName(event.target.value)
                    }
                    fullWidth
                  />
                  <TextField
                    label={t('enter_company_url')}
                    size="small"
                    variant="outlined"
                    placeholder={t('company_url_placeholder')}
                    value={companyWebsiteUrl}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setCompanyWebsiteUrl(event.target.value);
                    }}
                    sx={{ mt: theme.spacing(1) }}
                    fullWidth
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button
                    disabled={
                      !companyName || !companyWebsiteUrl || configuredMonitorings.length === 0
                    }
                    variant="contained"
                    onClick={() => {
                      if (!isValidUrl(companyWebsiteUrl)) {
                        setCompanyWebsiteUrl((prevState) => `https://${prevState}`);
                      }
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    disabled={false}
                    variant="contained"
                    color="warning"
                    onClick={() => {
                      navigate(-1);
                    }}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>{t('monitoring')}</StepLabel>
              <StepContent>
                <MonitoringMultiSelect
                  value={selectedMonitorings}
                  monitorings={configuredMonitorings}
                  onChange={handleSelectedMonitoringChange}
                />
                <Box sx={{ mb: 2 }}>
                  <Button
                    disabled={selectedMonitorings.length === 0}
                    variant="contained"
                    onClick={handleNextSurveyGuideStep}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>{t('finish_configuration')}</StepLabel>
              <StepContent>
                {t('guide_info_text')}
                <Box sx={{ mb: 2 }}>
                  <Button
                    disabled={loading}
                    variant="contained"
                    color="warning"
                    startIcon={<Repeat />}
                    onClick={async () => await handleAddTrustPageClick()}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('another_entry')}
                  </Button>
                  <Button
                    disabled={loading || !companyName || !companyWebsiteUrl}
                    variant="contained"
                    startIcon={loading ? '' : <Check />}
                    onClick={async () => {
                      correctContentUrl(imprintLinkRef);
                      correctContentUrl(privacyPolicyLinkRef);

                      const success = await handleAddTrustPageClick();
                      if (success) navigate(-1);
                    }}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {loading ? <CircularProgress size={24} /> : t('finish')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </Box>
        <Box sx={{ display: 'flex', minWidth: '1px', minHeight: '300px', mx: theme.spacing(6) }}>
          <Divider
            orientation="vertical"
            flexItem
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'flex-start',
            minWidth: '400px',
          }}
        >
          <Typography variant="body2">{t('optional_configuration')}</Typography>
          <FormGroup sx={{ mt: theme.spacing(1) }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <FormControlLabel
                label={<Typography variant="body2">{t('design_label')}</Typography>}
                control={
                  <Switch
                    checked={editDesign}
                    onChange={() => {
                      setEditDesign((prevState) => !prevState);
                    }}
                  />
                }
              />
              {editDesign && (
                <TrustpageDesignChanger
                  defaultDesign={design}
                  onChange={setDesign}
                />
              )}
              <FormControlLabel
                label={<Typography variant="body2">{t('content_label')}</Typography>}
                control={
                  <Switch
                    checked={editContent}
                    onChange={() => {
                      setEditContent((prevState) => !prevState);
                    }}
                  />
                }
                sx={{ mt: theme.spacing(2) }}
              />
              {editContent && (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <TextField
                    label={t('content_label')}
                    placeholder={t('about_company_placeholder')}
                    multiline
                    rows={6}
                    inputRef={aboutCompanyTextRef}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      if (aboutCompanyTextRef.current) {
                        aboutCompanyTextRef.current.value = event.target.value;
                      }
                    }}
                    sx={{ mt: theme.spacing(1) }}
                    fullWidth
                  />
                  <TextField
                    label={t('imprint_link_label')}
                    size="small"
                    placeholder={t('imprint_link_placeholder')}
                    inputRef={imprintLinkRef}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      if (imprintLinkRef.current) {
                        imprintLinkRef.current.value = event.target.value;
                      }
                    }}
                    sx={{ mt: theme.spacing(1) }}
                    fullWidth
                  />
                  <TextField
                    label={t('privacy_policy_link_label')}
                    size="small"
                    placeholder={t('privacy_policy_link_placeholder')}
                    inputRef={privacyPolicyLinkRef}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      if (privacyPolicyLinkRef.current) {
                        privacyPolicyLinkRef.current.value = event.target.value;
                      }
                    }}
                    sx={{ mt: theme.spacing(1) }}
                    fullWidth
                  />
                  <TextField
                    label={t('contact_email_label')}
                    size="small"
                    placeholder="musterkontakt@email.de"
                    inputRef={contactEmailRef}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      if (contactEmailRef.current) {
                        contactEmailRef.current.value = event.target.value;
                      }
                    }}
                    sx={{ mt: theme.spacing(1) }}
                    fullWidth
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: theme.spacing(1),
                    }}
                  >
                    <Typography>{t('language')}</Typography>
                    <ToggleButtonGroup
                      color="primary"
                      size="small"
                      value={language}
                      exclusive
                      onChange={(event: MouseEvent<HTMLElement>, newLang: string) => {
                        setLanguage(newLang);
                      }}
                      sx={{
                        '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                          border: `1px solid ${theme.palette.primary.main}`,
                        },
                      }}
                    >
                      <ToggleButton value="de">
                        <FlagDe
                          width="32px"
                          height="100%"
                        />
                      </ToggleButton>
                      <ToggleButton value="en">
                        <FlagGb
                          width="32px"
                          height="100%"
                        />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
              )}
            </Box>
          </FormGroup>
        </Box>
      </Guide>
      <Snackbar
        open={showCreateError}
        autoHideDuration={10000}
        onClose={handleCreateErrorClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCreateErrorClose}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {t('trustpage_create_error')}
        </Alert>
      </Snackbar>
    </Section>
  );
};

export default TrustpageGuide;
